//<editor-fold desc="Imports">
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {
  ActionListItemModel,
  ActionMenuItemAction,
  ActionMenuItemsPipe,
  AMOUNT_DECIMALS,
  AMOUNT_FORMAT,
  BadgeColor,
  BreadcrumbsComponent,
  ButtonActionEntity,
  CapitalizePipe,
  ClientInvoice,
  ClientInvoiceDraft,
  ClientInvoiceDraftItem,
  ClientInvoiceDraftItemService,
  ClientInvoiceDraftService,
  ClientInvoiceItem,
  ClientInvoiceItemService,
  ClientInvoiceService,
  ClientOrderItem,
  ClientOrderItemService,
  ConfirmDialogComponent,
  CopyableTitleComponent,
  DetailTitleService,
  DialogResult,
  DocumentSections,
  DocumentWrapperComponent,
  Entity,
  errorToast,
  FormErrorService,
  FormStateService,
  JoinedSupplierInvoiceItemMapping,
  ListContext,
  ListSkeletonComponent,
  NavigationService,
  OverviewCreateButtonComponent,
  PercentagePipe,
  RcSelectableOverflowDirective,
  replaceByIri,
  roundAmount,
  RouteContextMenuService,
  RouteEntitySubscriberService,
  SectionItemStatus,
  setSectionValues,
  showPopupTimout,
  stopOrRemovePopup,
  StoredSupplierCreditNoteItemLink,
  successDeleteToast,
  successSaveToast,
  SupplierCreditNoteMappingType,
  SupplierInvoice,
  SupplierInvoiceItem,
  SupplierInvoiceItemMapping,
  SupplierInvoiceItemMappingService,
  SupplierInvoiceItemService,
  SupplierInvoiceSection,
  SupplierInvoiceSectionService,
  SupplierInvoiceService,
  SupplierInvoiceStatus,
  ValidationDirective,
} from 'shared';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NumberFormatOptions, NumberPipe } from '@progress/kendo-angular-intl';
import { SupplierInvoiceTotalsComponent } from '@features/purchase/supplier-invoices/supplier-invoice-totals/supplier-invoice-totals.component';
import { ButtonComponent, DropDownButtonComponent } from '@progress/kendo-angular-buttons';
import {
  CheckBoxComponent,
  FormFieldComponent,
  NumericTextBoxComponent,
  TextAreaComponent,
} from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass, NgStyle, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { catchError, EMPTY, forkJoin, map, Observable, of, Subject, Subscription, take, throwError } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  ExpansionPanelComponent,
  ExpansionPanelTitleDirective,
  SelectEvent,
  TabContentDirective,
  TabStripComponent,
  TabStripTabComponent,
  TabTitleDirective,
} from '@progress/kendo-angular-layout';
import { CdkDragDrop, CdkDragPlaceholder, CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';
import {
  PopoverAnchorDirective,
  PopoverBodyTemplateDirective,
  PopoverComponent,
} from '@progress/kendo-angular-tooltip';
import { SupplierInvoiceSidebarPanelComponent } from '@features/purchase/supplier-invoices/supplier-invoice-detail/supplier-invoice-sidebar-panel/supplier-invoice-sidebar-panel.component';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { ClientInvoiceDraftCreateModalComponent } from '@features/purchase/supplier-invoices/client-invoice-draft-create-modal/client-invoice-draft-create-modal.component';
import { ClientInvoiceItemComponent } from '@features/purchase/supplier-invoices/client-invoice-item/client-invoice-item.component';
import { ClientInvoiceDraftItemComponent } from '@features/purchase/supplier-invoices/client-invoice-draft-item/client-invoice-draft-item.component';
import { SupplierInvoiceOrderLinkPopupComponent } from '@features/purchase/supplier-invoices/supplier-invoice-order-link-popup/supplier-invoice-order-link-popup';
import { SupplierInvoiceOrderItemLinkPopupComponent } from '@features/purchase/supplier-invoices/supplier-invoice-order-item-link-popup/supplier-invoice-order-item-link-popup.component';
import { supplierInvoiceRefreshPlanBuilder } from '@features/purchase/supplier-invoices/supplier-invoice-detail/Supplier-invoice-setup-and-helpers/SupplierInvoiceRefreshPlanBuilder/supplier-invoice.refresh-plan-builder';
import { SupplierInvoiceCoordinator } from '@features/purchase/supplier-invoices/supplier-invoice-detail/Supplier-invoice-setup-and-helpers/SupplierInvoiceCoordinator/supplier-invoice.coordinator';
import { CreateSupplierComplaintModalComponent } from '@features/purchase/supplier-invoices/supplier-invoice-detail/create-supplier-complaint-modal/create-supplier-complaint-modal.component';
import { createSupplierInvoiceSelectors } from '@features/purchase/supplier-invoices/supplier-invoice-detail/Supplier-invoice-setup-and-helpers/SupplierInvoiceSelectors/supplier-invoice.selectors';
import { MappingFormsManager } from '@features/purchase/supplier-invoices/supplier-invoice-detail/Supplier-invoice-setup-and-helpers/MappingFormManager/supplier-invoice.mapping-form.manager';
import { SupplierInvoiceMappingOpsService } from '@features/purchase/supplier-invoices/supplier-invoice-detail/Supplier-invoice-setup-and-helpers/MappingFormManager/supplier-invoice.mapping-ops.service';

//</editor-fold>

@Component({
  selector: 'rc-supplier-invoice-detail',
  standalone: true,
  imports: [
    PercentagePipe,
    DocumentWrapperComponent,
    BreadcrumbsComponent,
    OverviewCreateButtonComponent,
    TranslateModule,
    SupplierInvoiceTotalsComponent,
    ButtonComponent,
    CapitalizePipe,
    FormFieldComponent,
    LabelComponent,
    ReactiveFormsModule,
    TextAreaComponent,
    UpperCasePipe,
    ValidationDirective,
    TabStripTabComponent,
    TabContentDirective,
    TabTitleDirective,
    CdkDropList,
    CdkDragPlaceholder,
    DragDropModule,
    FormsModule,
    TabStripComponent,
    SupplierInvoiceSidebarPanelComponent,
    ClientInvoiceItemComponent,
    NumberPipe,
    NgClass,
    ClientInvoiceDraftItemComponent,
    ExpansionPanelComponent,
    NumericTextBoxComponent,
    NgStyle,
    PopoverComponent,
    PopoverAnchorDirective,
    PopoverBodyTemplateDirective,
    DropDownButtonComponent,
    ActionMenuItemsPipe,
    ExpansionPanelTitleDirective,
    CheckBoxComponent,
    CopyableTitleComponent,
    ListSkeletonComponent,
    RcSelectableOverflowDirective,
    NgTemplateOutlet,
  ],
  providers: [CapitalizePipe, FormErrorService, TranslatePipe, DialogRef],
  templateUrl: './supplier-invoice-detail.component.html',
  styleUrl: './supplier-invoice-detail.component.scss',
})
export class SupplierInvoiceDetailComponent extends DocumentWrapperComponent<SupplierInvoice, ClientOrderItem> {
  //<editor-fold desc="BASE STATE">
  isLoading = signal(true);
  docContainer = viewChild<ElementRef<HTMLElement>>('docContainer');
  tabContainer = viewChild<ElementRef<HTMLElement>>('tabContainer');
  docContentHeight = signal(0);
  tabHeight = signal(0);
  state = signal<ListContext | null>(null);
  selectedTab = signal<number>(0);
  showOnlySectionsWithoutOrderOrNotFullyMapped = signal<boolean>(false);
  currentDraggedMapping = signal<{ mapping: SupplierInvoiceItemMapping; sectionIri: string } | null>(null);
  //</editor-fold>

  //<editor-fold desc="UI STATE">
  clientInvoiceItems = signal<ClientInvoiceItem[]>([]);
  clientInvoices = signal<ClientInvoice[]>([]);
  sectionOpenStatus = signal(new Map<string, boolean>());

  allSectionsOpen = computed(() => {
    const values = Array.from(this.sectionOpenStatus().values());
    return values.length > 0 && values.every((v) => v);
  });

  allSectionsClosed = computed(() => {
    const values = Array.from(this.sectionOpenStatus().values());
    return values.length > 0 && values.every((v) => !v);
  });

  currentToggleState = signal<{ text: string; action: () => void }>({
    text: 'CLOSE_ALL_SECTIONS',
    action: () => this.closeAllSections(),
  });

  allToggleLabel = computed(() => {
    if (this.allSectionsClosed()) return 'OPEN_ALL_SECTIONS';
    if (this.allSectionsOpen()) return 'CLOSE_ALL_SECTIONS';
    return this.currentToggleState().text;
  });

  initialDetailsFetchDone = signal(false);
  #lastFetchedInvoiceIri = signal<string | null>(null);
  #notesInvoiceIri: string | undefined = undefined;
  //</editor-fold>

  //<editor-fold desc="Formats & forms">
  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    maximumFractionDigits: 2,
    useGrouping: true,
    minimumFractionDigits: 2,
  };
  protected readonly amountFormat = AMOUNT_FORMAT;
  protected readonly amountDecimals = AMOUNT_DECIMALS;

  supplierInvoiceForm: FormGroup<SupplierInvoiceForm> = new FormGroup<SupplierInvoiceForm>({
    internalNote: new FormControl('', { validators: [] }),
  });
  private formValues = toSignal(this.supplierInvoiceForm.valueChanges, {
    initialValue: this.supplierInvoiceForm.getRawValue(),
  });
  notesBadgeCount = computed(() => {
    const hasFormNote = !!this.formValues().internalNote?.trim();
    const hasOrderNotes = !!this.supplierInvoice()?.clientOrderInternalNotes?.some((n) => !!n?.trim());
    return (hasFormNote ? 1 : 0) + (hasOrderNotes ? 1 : 0);
  });
  #updateInvoice$ = new Subject<'internalNote'>();

  // <<< CHANGED: use MappingFormsManager, keep public Map for template compatibility
  mappingFormsManager = new MappingFormsManager();
  mappingForms: Map<string, FormGroup> = this.mappingFormsManager.getAll() as unknown as Map<string, FormGroup>;
  //</editor-fold>

  //<editor-fold desc="Derived collections / computed">
  // A clientOrderItem that's already fully mapped from elsewhere (another invoice) but has
  // no mapping yet in THIS invoice stays visible so it can still be dropped (double-invoice,
  // with a warning). Once it has a mapping here too, it disappears like any other fully-mapped item.
  private clientOrderItemIrisWithMappingInThisInvoice = computed(() => {
    const mappingsBySection = this.supplierInvoiceItemMappingsSections() ?? {};
    const iris = new Set<string>();
    for (const mappings of Object.values(mappingsBySection)) {
      for (const m of mappings ?? []) {
        if (m.clientOrderItem) iris.add(m.clientOrderItem);
      }
    }
    return iris;
  });

  filteredClientOrderItems = computed(() => {
    const firstSectionIri = this.firstSectionIri();
    if (!firstSectionIri) return [] as ClientOrderItem[];

    const itemsByIri = this.clientOrderItemsBySectionByIri().get(firstSectionIri) ?? new Map<string, ClientOrderItem>();
    const mappedInThisInvoice = this.clientOrderItemIrisWithMappingInThisInvoice();

    return [...itemsByIri.values()].filter(
      (item) => !this.isClientItemFullyMapped(item) || !mappedInThisInvoice.has(item['@id']!),
    );
  });

  private unmappedClientOrderItems = computed(() =>
    this.filteredClientOrderItems().filter((item) => !this.isClientItemFullyMapped(item)),
  );

  // itemsTotal = computed(() =>
  //   this.sum(
  //     this.allSupplierInvoiceItems()?.map((i) => {
  //       let vatPercentage = 0;
  //       let amountInclVat = 0;
  //       i.taxOverview.forEach((t) => {
  //         vatPercentage += t.vatPercentage;
  //         amountInclVat += t.amountInclVat;
  //       });
  //       vatPercentage = 1 + vatPercentage / 100;
  //       console.log(vatPercentage);
  //       console.log((i.basicNetPrice * vatPercentage + amountInclVat) * i.amount);
  //
  //       return (i.basicNetPrice * vatPercentage + amountInclVat) * i.amount;
  //     }),
  //   ),
  // );
  clientInvoiceDraftItemsTotal = computed(() =>
    this.sum(this.allClientInvoiceDraftItems()?.map((i) => i.amount) ?? []),
  );
  clientInvoiceItemsTotal = computed(() => this.sum(this.clientInvoiceItems()?.map((i) => i.amount) ?? []));

  allInvoiceItemsFullyMapped = computed<boolean>(() => {
    const bySection = this.fullyMappedBySection();
    const sections = this.sections() ?? [];
    if (sections.length === 0) return false;

    const anyItems = Object.values(this.supplierInvoiceItemsSections() ?? {}).some((arr) => (arr?.length ?? 0) > 0);
    if (!anyItems) return false;

    return sections.every((s) => bySection.get(s['@id']!) === true);
  });

  allDoNotInvoiceBySection = computed(() => {
    const itemsBySection = this.supplierInvoiceItemsSections() ?? {};
    const out = new Map<string, boolean>();

    for (const [sectionIri, items] of Object.entries(itemsBySection)) {
      const arr = items ?? [];
      out.set(sectionIri, arr.length > 0 && arr.every((it) => it.doNotInvoice));
    }

    return out;
  });
  //</editor-fold>

  //<editor-fold desc="Manual overrides for 'linked' flag">
  linkedSectionOverrides = signal<Map<string, boolean>>(new Map());

  sectionHasMappings = computed(() => {
    const bySection = this.supplierInvoiceItemMappingsSections() ?? {};
    const out = new Map<string, boolean>();
    for (const [sectionIri, arr] of Object.entries(bySection)) {
      out.set(sectionIri, (arr?.length ?? 0) > 0);
    }
    return out;
  });

  fullyMappedUnprocessedSectionCount = computed(() => {
    const fully = this.fullyMappedBySection();
    const sections = this.sections() ?? [];
    let count = 0;

    for (const s of sections) {
      const iri = s['@id']!;
      if ((fully.get(iri) ?? false) && !s.fullyProcessed) count++;
    }
    return count;
  });

  isDraggingMappedClientItem = signal<boolean>(false);
  supplierInvoiceStatus = computed<Record<string, { text: string; class: string }>>(() => ({
    processed: { text: this.upper(SupplierInvoiceStatus.PROCESSED), class: 'status-button-green' },
    processing: { text: this.upper(SupplierInvoiceStatus.PROCESSING), class: 'status-button-purple' },
    new: { text: this.upper(SupplierInvoiceStatus.NEW), class: 'status-button-yellow' },
    do_not_invoice: { text: this.upper(SupplierInvoiceStatus.DO_NOT_INVOICE), class: 'status-button-green' },
  }));

  maxAllowedAmountByMappingIri = computed(() => {
    const result = new Map<string, number>();
    const allMappings = this.allSupplierInvoiceMappings() ?? [];

    for (const mapping of allMappings) {
      if (!mapping['@id']) continue;
      result.set(mapping['@id'], this.getMaxAllowedAmount(mapping));
    }

    return result;
  });

  isDraggingEntered: string | null = null;
  clientOrderDropzoneEntered = false;
  //</editor-fold>

  //<editor-fold desc="DI">
  #destroyRef = inject(DestroyRef);
  #capitalizePipe = inject(CapitalizePipe);
  #clientInvoiceItemService = inject(ClientInvoiceItemService);
  #clientInvoiceDraftService = inject(ClientInvoiceDraftService);
  #clientInvoiceDraftItemService = inject(ClientInvoiceDraftItemService);
  #clientOrderItemService = inject(ClientOrderItemService);
  #clientInvoiceService = inject(ClientInvoiceService);
  #menu = inject(RouteContextMenuService);
  #dialog = inject(DialogRef);
  #dialogService = inject(DialogService);
  #formErrorService = inject(FormErrorService);
  #formStateService = inject(FormStateService);
  #navigationService = inject(NavigationService);
  #notificationService = inject(NotificationService);
  #supplierInvoiceService = inject(SupplierInvoiceService);
  #supplierInvoiceItemService = inject(SupplierInvoiceItemService);
  #supplierInvoiceItemMappingService = inject(SupplierInvoiceItemMappingService);
  #supplierInvoiceSectionsService = inject(SupplierInvoiceSectionService);
  #tracker = inject(RouteEntitySubscriberService);
  #translateService = inject(TranslateService);
  #coordinator = inject(SupplierInvoiceCoordinator);
  #mappingOps = inject(SupplierInvoiceMappingOpsService);
  #detailTitleService = inject(DetailTitleService);
  // Tracks the in-flight #getFromBackEnd() request so it can be cancelled when the user
  // navigates to a different invoice before it resolves (the component instance is reused
  // across next/previous navigation, so an uncancelled request would otherwise land late
  // and overwrite the new invoice's data with the previous one's).
  #getFromBackEndSub?: Subscription;

  readonly titleParts = this.#detailTitleService.titleParts;

  supplierInvoice = this.#tracker.getSignal<SupplierInvoice>('supplierInvoice') as WritableSignal<SupplierInvoice>;
  sections = this.#tracker.getSectionsSignals<SupplierInvoiceSection>('supplierInvoice');
  supplierInvoiceItemsSections = this.#tracker.getSectionItemsSignal<SupplierInvoiceItem>('supplierInvoice');
  supplierInvoiceItemMappingsSections =
    this.#tracker.getItemMappingsSignal<SupplierInvoiceItemMapping>('supplierInvoice');
  clientOrderItemsSections = this.#tracker.getLinkedItemsSignal<ClientOrderItem>('supplierInvoice');
  clientInvoiceDraftItemsSections = this.#tracker.getDraftItemsSignal<ClientInvoiceDraftItem>('supplierInvoice');
  //</editor-fold>

  //<editor-fold desc="Selectors / flattened / indices">

  // <<< CHANGED: create selectors from shared helper
  selectors = createSupplierInvoiceSelectors({
    sections: this.sections as unknown as Signal<SupplierInvoiceSection[] | null>,
    supplierInvoiceItemsSections: this
      .supplierInvoiceItemsSections as unknown as Signal<DocumentSections<SupplierInvoiceItem> | null>,
    supplierInvoiceItemMappingsSections: this
      .supplierInvoiceItemMappingsSections as unknown as Signal<DocumentSections<SupplierInvoiceItemMapping> | null>,
    clientOrderItemsSections: this
      .clientOrderItemsSections as unknown as Signal<DocumentSections<ClientOrderItem> | null>,
    clientInvoiceDraftItemsSections: this
      .clientInvoiceDraftItemsSections as unknown as Signal<DocumentSections<ClientInvoiceDraftItem> | null>,
  });

  fullyMappedBySection = this.selectors.fullyMappedBySection;
  hasSections = this.selectors.hasSections;
  fullyMappedSectionCount = this.selectors.fullyMappedSectionCount;
  fullyMappedUnprocessedSectionIris = this.selectors.fullyMappedUnprocessedSectionIris;
  fullyMappedUnprocessedSectionMappingsBySection = this.selectors.fullyMappedUnprocessedSectionMappingsBySection;
  fullyMappedUnprocessedMappingsByItem = this.selectors.fullyMappedUnprocessedMappingsByItem;
  hasActivityBySection = this.selectors.hasActivityBySection;
  droppableItemIris = this.selectors.droppableItemIris;
  invoiceLineIds = this.selectors.invoiceLineIds;

  // Use selectors for core indices / stats
  itemSectionByIri = this.selectors.itemSectionByIri;
  allSupplierInvoiceItems = this.selectors.allSupplierInvoiceItems;
  allSupplierInvoiceMappings = this.selectors.allSupplierInvoiceMappings;
  clientOrderItemsBySectionByIri = this.selectors.clientOrderItemsBySectionByIri;

  mappingsBySectionByItem = computed(() => {
    const sections = this.supplierInvoiceItemMappingsSections() ?? {};
    const coiIndex = this.clientOrderItemsBySectionByIri();
    const result = new Map<string, Map<string, JoinedSupplierInvoiceItemMapping[]>>();

    for (const [sectionIri, mappings] of Object.entries(sections)) {
      const byItem = new Map<string, JoinedSupplierInvoiceItemMapping[]>();
      const coiByIri = coiIndex.get(sectionIri);

      for (const m of mappings ?? []) {
        const itemIri = m.supplierInvoiceItem;
        const list = byItem.get(itemIri) ?? [];
        list.push({
          mapping: m,
          clientOrderItem: coiByIri?.get(m.clientOrderItem ?? '') ?? null,
        });
        byItem.set(itemIri, list);
      }
      result.set(sectionIri, byItem);
    }
    return result;
  });

  allClientInvoiceDraftItems = computed(() => {
    return Object.values(this.clientInvoiceDraftItemsSections() ?? {}).flat();
  });
  //</editor-fold>

  //<editor-fold desc="View model flags">
  supplierInvoiceIsLocked = computed(() =>
    this.supplierInvoice()
      ? [SupplierInvoiceStatus.PROCESSED].includes(this.supplierInvoice().status as SupplierInvoiceStatus)
      : false,
  );

  visibleSections = computed(() => {
    const sections = this.sections() ?? [];
    let list = sections;

    const initial = this.initialSectionOrder();

    // if we have an initial order, re-apply it
    if (initial) {
      const indexMap = new Map(initial.map((s, i) => [s['@id']!, i]));
      list = [...sections].sort(
        (a, b) =>
          (indexMap.get(a['@id']!) ?? Number.MAX_SAFE_INTEGER) - (indexMap.get(b['@id']!) ?? Number.MAX_SAFE_INTEGER),
      );
    }

    // live filter can keep working
    if (this.showOnlySectionsWithoutOrderOrNotFullyMapped()) {
      list = list.filter((s) => !s.clientOrder || !this.isSectionFullyMapped(s));
    }

    return list;
  });

  isMulti = computed(() => (this.sections() ? this.sections()!.length > 1 : false));
  firstSectionIri = computed(() => this.sections()?.[0]?.['@id'] ?? null);
  clientInvoiceDraft = signal<ClientInvoiceDraft | null>(null);
  activeClientName = computed(
    () => this.clientInvoiceDraft()?.clientName ?? this.clientInvoices()[0]?.clientName ?? null,
  );

  #dCo?: ResizeObserver;
  #to?: ResizeObserver;
  //</editor-fold>

  //<editor-fold desc="Derived VM flags depending on selectors">
  private hasDraftOrInvoiceItems = computed(() =>
    this.supplierInvoiceIsLocked()
      ? this.clientInvoiceItems().length > 0
      : (this.allClientInvoiceDraftItems()?.length ?? 0) > 0,
  );

  private allClientItemsMapped = computed(() => (this.unmappedClientOrderItems()?.length ?? 0) === 0);
  private readonly initialSectionOrder = signal<SupplierInvoiceSection[] | null>(null);
  private readonly initialSectionOpenDone = signal(false);
  private readonly initialCheckedSectionsDone = signal(false);

  checkedSectionIris = signal<Set<string>>(new Set());

  draftAmountReadOnlyByIri = computed<Map<string, boolean>>(() => {
    const section = this.sections()?.[0];
    if (!section?.clientOrder) return new Map();
    const mappings = this.allSupplierInvoiceMappings();
    // Build a set of draft item IRIs that are referenced by a real mapping with a clientOrderItem.
    // draftItem.supplierInvoiceItem is not returned by the API; mapping.clientInvoiceDraftItem is.
    const linkedDraftItemIris = new Set(
      mappings.filter((m) => !!m.clientOrderItem && !!m.clientInvoiceDraftItem).map((m) => m.clientInvoiceDraftItem),
    );
    const out = new Map<string, boolean>();
    for (const draftItem of this.allClientInvoiceDraftItems()) {
      if (linkedDraftItemIris.has(draftItem['@id']!)) out.set(draftItem['@id']!, true);
    }
    return out;
  });

  checkedSectionMappings = computed<SupplierInvoiceItemMapping[]>(() => {
    const checked = this.checkedSectionIris();
    const bySection = this.supplierInvoiceItemMappingsSections() ?? {};
    const fullyMapped = this.fullyMappedBySection();
    const sectionsByIri = new Map((this.sections() ?? []).map((s) => [s['@id']!, s]));
    const out: SupplierInvoiceItemMapping[] = [];
    for (const iri of checked) {
      const section = sectionsByIri.get(iri);
      if (section?.fullyProcessed) continue;
      if (fullyMapped.get(iri)) {
        out.push(...((bySection[iri] as SupplierInvoiceItemMapping[]) ?? []));
      }
    }
    return out;
  });
  //</editor-fold>

  //<editor-fold desc="Action menu item functions">
  protected menuItemsFn = (item: SupplierInvoiceItem): ActionListItemModel<ActionMenuItemAction>[] => {
    const sectionIri = this.itemSectionByIri().get(item['@id']!);
    const section = sectionIri ? (this.sections() ?? []).find((s) => s['@id'] === sectionIri) : undefined;
    const items: ActionListItemModel<ActionMenuItemAction>[] = [];

    if (this.isMulti()) {
      items.push({
        text: this.#capitalizePipe.transform(
          this.#translateService.instant('CREATE_ASSOCIATION', {
            value: this.#translateService.instant('ORDER_LINE(S)'),
          }),
        ),
        action: ActionMenuItemAction.ASSOCIATE,
        disabled: !section?.clientOrder || this.#hasDraftForSupplierItem(item) || item.doNotInvoice,
        iconClass: 'rc-icon-row-insert-bottom',
      });
    } else {
      items.push({
        text: this.#capitalizePipe.transform(
          this.#translateService.instant('CLONE_ENTITY', { entity: this.#translateService.instant('LINE') }),
        ),
        action: ActionMenuItemAction.DUPLICATE,
        disabled: this.#hasDraftForSupplierItem(item) || item.doNotInvoice,
        iconClass: 'rc-icon-row-insert-bottom',
      });
    }

    items.push({
      text: this.#capitalizePipe.transform(
        this.#translateService.instant('COPY_ENTITY', {
          entity: this.#translateService.instant('SUPPLIER_INVOICE_LINE'),
        }),
      ),
      action: ActionMenuItemAction.COPY,
      iconClass: 'rc-icon-link',
    });

    if (item.doNotInvoice) {
      items.push({
        text: this.#capitalizePipe.transform(this.#translateService.instant('DO_INVOICE')),
        action: ActionMenuItemAction.DO_INVOICE,
        disabled: this.isMulti()
          ? !!item.supplierInvoiceItemMappings.length
          : !!item.supplierInvoiceItemMappings.length || this.#hasDraftForSupplierItem(item),
        iconClass: 'rc-icon-invoice',
      });
    } else {
      items.push({
        text: this.#capitalizePipe.transform(this.#translateService.instant('DO_NOT_INVOICE')),
        action: ActionMenuItemAction.DO_NOT_INVOICE,
        disabled: this.isMulti()
          ? !!item.supplierInvoiceItemMappings.length
          : !!item.supplierInvoiceItemMappings.length || this.#hasDraftForSupplierItem(item),
        iconClass: 'rc-icon-no-invoice',
      });
    }
    return items;
  };

  protected mappedMenuItemsFn = (item: SupplierInvoiceItemMapping): ActionListItemModel<ActionMenuItemAction>[] => {
    return [
      {
        text: this.#capitalizePipe.transform(
          this.#translateService.instant('REMOVE_ASSOCIATED', {
            value: this.#translateService.instant('ORDER_LINE'),
          }),
        ),
        action: ActionMenuItemAction.DELETE,
        disabled: !!item.clientInvoiceItem,
        iconClass: 'rc-icon-row-remove',
      },
    ];
  };
  //</editor-fold>

  //<editor-fold desc="Constructor / effects">
  constructor() {
    super();
    // Drop the previous page's title so the header shows its skeleton until this one loads.
    this.titleParts.set([]);

    // bind mapping ops to tracker signals  <<< CHANGED
    this.#mappingOps.bind({
      supplierInvoiceItemMappingsSections: this
        .supplierInvoiceItemMappingsSections as unknown as Signal<DocumentSections<SupplierInvoiceItemMapping> | null>,
      clientOrderItemsSections: this
        .clientOrderItemsSections as unknown as Signal<DocumentSections<ClientOrderItem> | null>,
    });

    // Reset once-flags when the tracker clears the invoice on navigation.
    effect(() => {
      if (!this.supplierInvoice()) {
        // Cancel any in-flight fetch/mercure-triggered refresh for the invoice we're
        // navigating away from, so it can't land late and overwrite the next invoice's data.
        this.#getFromBackEndSub?.unsubscribe();
        this.#coordinator.cancelPendingRefreshes();
        this.#lastFetchedInvoiceIri.set(null);
        this.initialDetailsFetchDone.set(false);
        this.initialSectionOrder.set(null);
        this.initialSectionOpenDone.set(false);
        this.initialCheckedSectionsDone.set(false);
        this.clientInvoices.set([]);
        this.clientInvoiceItems.set([]);
        this.clientInvoiceDraft.set(null);
        this.sectionOpenStatus.set(new Map());
        this.checkedSectionIris.set(new Set());
      }
    });

    effect(() => {
      const invoice = this.supplierInvoice();
      const sections = this.sections();

      if (!invoice || !sections || sections.length === 0) return;
      if (this.#lastFetchedInvoiceIri() === invoice['@id']) return;

      // Reset all transient UI state so the new invoice starts clean
      this.initialSectionOrder.set(null);
      this.initialSectionOpenDone.set(false);
      this.initialCheckedSectionsDone.set(false);
      this.clientInvoices.set([]);
      this.clientInvoiceItems.set([]);
      this.clientInvoiceDraft.set(null);
      this.sectionOpenStatus.set(new Map());
      this.checkedSectionIris.set(new Set());

      this.#lastFetchedInvoiceIri.set(invoice['@id']!);
      this.initialDetailsFetchDone.set(true);
      // no global loader, we'll use per-section loaders
      this.#getFromBackEnd(); // uses default withLoader = false
    });

    effect(() => {
      const sections = this.sections() ?? [];
      if (!sections.length) return;

      // already sorted once → never recompute
      if (this.initialSectionOrder()) return;

      // 👇 wait until *all* sections are done loading
      const anyLoading = sections.some((s) => this.isSectionDataLoading(s));
      if (anyLoading) return;

      // ── at this point:
      // - sections exist
      // - all their data has been loaded at least once
      //   (items / mappings / fullyMappedBySection will be meaningful)

      const fullyBySection = this.fullyMappedBySection();
      const itemsBySection = this.supplierInvoiceItemsSections() ?? {};
      const originalIndex = new Map<string, number>(sections.map((s, i) => [s['@id']!, i]));

      const rank = (s: SupplierInvoiceSection) => {
        const iri = s['@id']!;
        const linked = !!s.clientOrder;
        const isFully = fullyBySection.get(iri) ?? false;

        if (!linked) return 0;
        return isFully ? 2 : 1;
      };

      const progressScore = (s: SupplierInvoiceSection) => {
        const iri = s['@id']!;
        const its = itemsBySection[iri] ?? [];
        const hasDoNotInvoice = its.some((it) => it.doNotInvoice);
        const hasAnyMappings = (this.supplierInvoiceItemMappingsSections()?.[iri]?.length ?? 0) > 0;

        return (hasAnyMappings ? 0 : 1) + (hasDoNotInvoice ? 0 : 1);
      };

      const sorted = [...sections].sort((a, b) => {
        const ra = rank(a);
        const rb = rank(b);
        if (ra !== rb) return ra - rb;

        const pa = progressScore(a);
        const pb = progressScore(b);
        if (pa !== pb) return pb - pa;

        return originalIndex.get(a['@id']!)! - originalIndex.get(b['@id']!)!;
      });

      // ✅ allowed here, in an effect
      this.initialSectionOrder.set(sorted);
    });

    // Measure tab + document heights
    effect(() => {
      const tabContainer = this.tabContainer();
      const docContainer = this.docContainer();
      if (tabContainer && docContainer) {
        this.setupTabMeasurement(tabContainer.nativeElement, docContainer.nativeElement);
      } else {
        this.teardownTabMeasurement();
        this.tabHeight.set(0);
        this.docContentHeight.set(0);
      }
    });

    // Auto-switch tab when everything is mapped
    effect(() => {
      if (this.allInvoiceItemsFullyMapped()) {
        queueMicrotask(() => this.setTab(1));
      }
    });

    // Init section open status (open if not fully mapped) — runs once after first data load
    effect(() => {
      const sections = this.sections();
      if (!sections || sections.length === 0) return;
      if (this.initialSectionOpenDone()) return;

      this.initialSectionOpenDone.set(true);
      const next = new Map<string, boolean>();
      for (const section of sections) {
        next.set(section['@id']!, !this.isSectionFullyMapped(section));
      }
      this.sectionOpenStatus.set(next);
    });

    // Init checked sections — pre-check all fully-mapped, unprocessed, linked sections on first data load
    effect(() => {
      const sections = this.sections() ?? [];
      if (!sections.length) return;
      if (this.initialCheckedSectionsDone()) return;
      const anyLoading = sections.some((s) => this.isSectionDataLoading(s));
      if (anyLoading) return;
      this.initialCheckedSectionsDone.set(true);
      const fullyMapped = this.fullyMappedBySection();
      const initial = new Set<string>();
      for (const s of sections) {
        if (s.clientOrder && !s.fullyProcessed && (fullyMapped.get(s['@id']!) ?? false)) {
          initial.add(s['@id']!);
        }
      }
      this.checkedSectionIris.set(initial);
    });

    // Fetch clientInvoiceDraft as soon as the first section has one
    effect(() => {
      const section = this.sections()?.[0];
      if (!section?.clientInvoiceDraft || this.clientInvoiceDraft()) return;
      this.#clientInvoiceDraftService
        .getForSupplierInvoiceSection(section['@id']!)
        .pipe(take(1))
        .subscribe({ next: (draft) => this.clientInvoiceDraft.set(draft) });
    });

    // Close sections when they become fully mapped
    effect(() => {
      const fully = this.fullyMappedBySection();
      this.sectionOpenStatus.update((prev) => {
        const next = new Map(prev);
        for (const [id, isFull] of fully) {
          if (isFull) next.set(id, false);
        }
        return next;
      });
    });

    // Mercure subscription list
    effect(() => {
      const list = this.allEntitiesToWatch();
      if (list.length) {
        this.#tracker.subscribeToItems('supplierInvoice', list);
      }
    });

    // Header, breadcrumbs, action button
    effect(() => {
      const invoice = this.supplierInvoice();
      if (!invoice) return;

      if (invoice.doNotInvoice && invoice.status !== SupplierInvoiceStatus.DO_NOT_INVOICE) {
        invoice.status = SupplierInvoiceStatus.DO_NOT_INVOICE;
        this.supplierInvoice.set(invoice);
      }

      this.state.set(this.#navigationService.getListContext());
      this.#setBreadCrumbs();
      this.titleParts.set([
        { text: invoice.documentNumber, copyable: true },
        { text: invoice.supplierName, copyable: false },
      ]);

      this.detailActionService.status.set({
        statusText: this.supplierInvoiceStatus()[invoice.status]?.text,
        colorClass: this.supplierInvoiceStatus()[invoice.status]?.class,
      });
      this.detailActionService.actionMenuItems.set(this.#getOverviewMenuItems());
      let disabled = true;
      if (!this.isMulti()) {
        disabled = !this.allClientInvoiceDraftItems().length || invoice.status === SupplierInvoiceStatus.PROCESSED;
      } else {
        disabled = this.checkedSectionMappings().length === 0 || invoice.status === SupplierInvoiceStatus.PROCESSED;
      }
      if (!this.supplierInvoiceIsLocked()) {
        this.detailActionService.creationButton.set({
          text: this.translationService.instant('CREATE_ENTITY_ACTION', {
            entity: this.translationService.instant('CLIENT_INVOICE'),
          }),
          action: () => this.#createClientInvoice(),
          style: 'primary',
          iconClass: 'rc-icon-check',
          customText: true,
          disabled: disabled,
        });
      }
    });

    // The note follows the invoice only, not its sections or lines: those must not reset a note being typed
    effect(() => {
      const invoice = this.supplierInvoice();
      if (invoice) this.#patchForm(invoice);
    });

    // When locked, load invoice items & remove creation button
    effect(() => {
      if (this.supplierInvoiceIsLocked()) {
        this.detailActionService.creationButton.set(undefined);
        this.#loadClientInvoiceItems();
      }
    });

    // Sync mapping forms whenever mappings change
    effect(() => {
      this.supplierInvoiceItemMappingsSections();
      this.syncMappingFormsFromSections();
    });

    // Lock/unlock mapping forms based on invoice status  <<< CHANGED
    effect(() => {
      const locked = this.supplierInvoiceIsLocked();
      this.mappingFormsManager.setLocked(locked);
    });

    // Bug 1 fix: debounce note saves instead of firing on every keystroke
    this.#updateInvoice$
      .pipe(
        debounceTime(1200),
        switchMap((field) => {
          const co = this.supplierInvoice();
          if (!co) return of(null);
          if (field === 'internalNote') co.internalNote = this.supplierInvoiceForm.controls.internalNote.value ?? '';
          return this.#supplierInvoiceService.patch(co['@id']!, co).pipe(
            catchError((err: HttpErrorResponse) => {
              this.notifyError(err);
              this.#formErrorService.setErrorViolations(err);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: (value) => {
          if (!value) return;
          // a saved note is no longer an unsaved edit, unless the user typed on in the meantime
          const { internalNote } = this.supplierInvoiceForm.controls;
          if ((internalNote.value ?? '') === (value.internalNote ?? '')) internalNote.markAsPristine();
          this.#patchForm(value);
          this.notifySuccess('SUPPLIER_INVOICE');
        },
      });

    const plan = supplierInvoiceRefreshPlanBuilder({ tracker: this.#tracker, coordinator: this.#coordinator });
    this.#tracker.registerSignalTopicBinding('supplierInvoice', plan);

    this.#formStateService.registerForm({
      form: this.supplierInvoiceForm,
      isDirty: () => this.supplierInvoiceForm.dirty,
      isInvalid: () => this.supplierInvoiceForm.invalid,
      submitLabel: () => 'UPDATE',
      submitIcon: () => 'rc-icon-save',
      onSubmit: () => this.updateSupplierInvoice('internalNote'),
      onCancel: () => this.cancelFormChanges(),
    });
  }

  private cancelFormChanges(): void {
    const invoice = this.supplierInvoice();
    // cancelling drops the unsaved note: mark it pristine first so the saved note is shown again
    this.supplierInvoiceForm.markAsPristine();
    if (invoice) {
      this.#patchForm(invoice);
    } else {
      this.supplierInvoiceForm.reset();
    }
    this.supplierInvoiceForm.markAsPristine();
  }
  //</editor-fold>

  //<editor-fold desc="Layout helpers">
  getMaxHeight(maxHeight: number) {
    if (this.tabHeight()) return maxHeight - (this.tabHeight() + 98);
    return maxHeight;
  }

  fallbackEnd() {
    setTimeout(() => this.isDraggingMappedClientItem.set(false), 100);
  }
  //</editor-fold>

  //<editor-fold desc="Mapping UI">
  // mappedAmount is a cross-invoice total owned by the backend — it can include
  // mappings from sections/invoices this component never loads. So we never
  // recompute it from scratch locally; we only nudge it by the known delta of
  // a single mapping change, as optimistic UI ahead of the backend refresh.
  private adjustClientItemMappedAmount(clientOrderItemIri: string, delta: number): void {
    if (!delta) return;

    const itemsBySection = this.clientOrderItemsSections() ?? {};
    const sectionIri = Object.keys(itemsBySection).find((iri) =>
      itemsBySection[iri]?.some((item) => item['@id'] === clientOrderItemIri),
    );
    if (!sectionIri) return;

    const updated = (itemsBySection[sectionIri] ?? []).map((item) => {
      if (item['@id'] !== clientOrderItemIri) return item;

      const totalAmount = Number(item.amount ?? 0) || 0;
      const mapped = Math.max(roundAmount((Number(item.mappedAmount) || 0) + delta), 0);
      const amountToMap = Math.max(roundAmount(totalAmount - mapped), 0);

      return { ...item, mappedAmount: mapped, amountToMap };
    });

    this.clientOrderItemsSections.update((sections) => setSectionValues(sections, sectionIri, updated));
  }

  #getOverviewMenuItems(): ButtonActionEntity[] {
    const actionMenuItems: ButtonActionEntity[] = [];

    actionMenuItems.push({
      text: this.#capitalizePipe.transform(
        this.#translateService.instant('COPY_ENTITY', {
          entity: this.#translateService.instant('SUPPLIER_INVOICE'),
        }),
      ),
      iconClass: 'rc-icon-link',
      action: () => this.#copySupplierInvoiceOrItem(),
    });

    actionMenuItems.push({
      text: this.#capitalizePipe.transform(this.#translateService.instant('COMPOSE_COMPLAINT')),
      iconClass: 'rc-icon-check',
      action: () => this.#openCreateSupplierComplaintDialog(),
    });

    return actionMenuItems;
  }

  onMappingDragStart(mapping: SupplierInvoiceItemMapping, sectionIri: string) {
    this.currentDraggedMapping.set({ mapping, sectionIri });
  }

  onMappingDragEnd() {
    this.currentDraggedMapping.set(null);
  }

  onDropBackToClientOrderList(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) return;

    const mapping = event.item.data as SupplierInvoiceItemMapping | undefined;
    if (!mapping?.['@id']) {
      console.warn('No mapping data on dropBackToClientOrderList', event);
      return;
    }

    const mappingIri = mapping['@id'] as string;
    const sectionIri = this.itemSectionByIri().get(mapping.supplierInvoiceItem);

    if (!sectionIri) {
      console.warn('Could not resolve section for mapping', mapping);
      return;
    }

    // --- 1) Optimistic UI: remove mapping from this section ---
    this.supplierInvoiceItemMappingsSections.update((sections) => {
      const current = (sections?.[sectionIri] ?? []) as SupplierInvoiceItemMapping[];
      const next = current.filter((m) => m['@id'] !== mappingIri);
      return setSectionValues(sections, sectionIri, next);
    });

    // --- 2) Optimistic UI: pull the removed mapping's amount back off the client item ---
    if (mapping.clientOrderItem) {
      this.adjustClientItemMappedAmount(mapping.clientOrderItem, -(Number(mapping.amount) || 0));
    }
    this.syncMappingFormsFromSections();

    // --- 3) Backend delete ---
    this.#supplierInvoiceItemMappingService.delete(mappingIri).subscribe({
      next: () => {
        this.notifyDelete(this.translationService.instant('SUPPLIER_INVOICE_ITEM_MAPPING'));

        // refresh linked clientOrderItems + draft items for correctness (authoritative backend values)
        this.#coordinator.refreshSectionLinkedItems(sectionIri);
        this.#coordinator.refreshSectionDraftItems(sectionIri);

        this.setDraftItemsForSection(sectionIri, this.clientInvoiceDraftItemsSections()?.[sectionIri] ?? [], true);

        this.#tracker.broadcastItemTopicChanged('supplierInvoice', sectionIri);

        this.isDraggingEntered = null;
        this.endDrag();
      },
      error: (err) => {
        // rollback to server state if delete fails
        this.#getFromBackEnd();
        this.notifyError(err);
        this.endDrag();
      },
    });
  }

  updateLinkedOrder(section: SupplierInvoiceSection) {
    const title = 'LINK_ANOTHER_ORDER';
    this.#dialog = this.#dialogService.open({
      content: SupplierInvoiceOrderLinkPopupComponent,
      title: this.#capitalizePipe.transform(this.#translateService.instant(title)),
    });
    this.#dialog.content.instance.supplierInvoice.set(this.supplierInvoice());
    this.#dialog.content.instance.supplierInvoiceSection.set(section);
    this.#dialog.result.subscribe((value: DialogResult) => {
      if (value.isSuccessFull) {
        this.getInvoice(true, value.result as SupplierInvoiceSection);
      }
    });
  }

  linkOrderItem(supplierInvoiceItem: SupplierInvoiceItem, section: SupplierInvoiceSection) {
    const title = this.#translateService.instant('CREATE_ASSOCIATION', {
      value: this.#translateService.instant('ORDER_LINE(S)'),
    });
    this.#dialog = this.#dialogService.open({
      content: SupplierInvoiceOrderItemLinkPopupComponent,
      title: this.#capitalizePipe.transform(this.#translateService.instant(title)),
    });
    this.#dialog.content.instance.section.set(section);
    this.#dialog.content.instance.clientOrder.set(section.clientOrder);
    this.#dialog.content.instance.supplierInvoiceItem.set(supplierInvoiceItem);
    this.#dialog.result.subscribe((value: DialogResult) => {
      if (value.isSuccessFull) {
        this.getInvoiceItemMapping(true, supplierInvoiceItem['@id']!, section);
        this.#tracker.broadcastItemTopicChanged('supplierInvoice', (value.result as SupplierInvoiceItemMapping)['@id']!);
      }
    });
  }

  deleteLinkedItem(supplierInvoiceItemMapping: SupplierInvoiceItemMapping, section: SupplierInvoiceSection) {
    this.deleteInvoiceItemMapping(supplierInvoiceItemMapping, section);
  }

  onDropOnInvoiceLine(
    event: CdkDragDrop<any[]>,
    targetLine: SupplierInvoiceItem,
    section: SupplierInvoiceSection,
  ): void {
    if (event.previousContainer === event.container) return;

    const dragged = event.item.data as SupplierInvoiceItemMapping | { '@id': string };

    const draggedIsMapping = this.isMapping(dragged);
    const mappingId = draggedIsMapping ? dragged['@id'] : undefined;
    const clientOrderItemId = draggedIsMapping ? dragged.clientOrderItem : (dragged as any)['@id'];
    const previousAmount = draggedIsMapping ? (dragged as SupplierInvoiceItemMapping).amount : undefined;

    const sectionIri = section['@id']!;
    let clientItem =
      this.clientOrderItemsBySectionByIri()
        .get(sectionIri)
        ?.get(clientOrderItemId ?? '') ?? null;

    if (!clientItem) {
      for (const byIri of this.clientOrderItemsBySectionByIri().values()) {
        const found = byIri.get(clientOrderItemId ?? '');
        if (found) {
          clientItem = found;
          break;
        }
      }
    }
    if (!clientItem) return;

    const isFullyMapped = this.isClientItemFullyMapped(clientItem);
    const isDraggedFromClientOrderList = event.previousContainer.id === 'clientOrderList';

    const proceed = () =>
      this.#performDrop(
        targetLine,
        section,
        draggedIsMapping,
        mappingId,
        clientOrderItemId!,
        clientItem!,
        previousAmount,
      );

    if (isFullyMapped && isDraggedFromClientOrderList) {
      this.#dialog = this.#dialogService.open({
        content: ConfirmDialogComponent,
        title: this.upper('CONFIRM_DIALOG_TITLE'),
      });
      this.#dialog.content.instance.entity = clientItem.articleNumber;
      this.#dialog.content.instance.message = this.#translateService.instant('CLIENT_ORDER_ITEM_FULLY_MAPPED');
      this.#dialog.result.subscribe((r: DialogResult) => r?.isSuccessFull && proceed());
    } else {
      proceed();
    }
  }

  doNotInvoice(supplierInvoiceItem: SupplierInvoiceItem, reverse = false): void {
    const itemIri = supplierInvoiceItem['@id']!;
    const sectionIri =
      this.itemSectionByIri().get(itemIri) ??
      Object.entries(this.supplierInvoiceItemsSections() ?? {}).find(([_, arr]) =>
        (arr ?? []).some((i) => i['@id'] === itemIri),
      )?.[0];

    this.#supplierInvoiceItemService.patch(supplierInvoiceItem['@id']!, { doNotInvoice: !reverse }).subscribe({
      next: (updated) => {
        if (sectionIri) {
          if (updated.supplierInvoiceStatus !== this.supplierInvoice()?.status) {
            this.supplierInvoice.update((prev) => ({
              ...prev!,
              status: updated.supplierInvoiceStatus as SupplierInvoiceStatus,
            }));
          }
          this.supplierInvoiceItemsSections.update((sections) => {
            const current = sections?.[sectionIri] ?? [];
            const next = current.map((i) => (i['@id'] === updated['@id'] ? updated : i));
            return setSectionValues(sections, sectionIri!, next);
          });

          this.#tracker.broadcastItemTopicChanged('supplierInvoice', sectionIri);
        }

        this.notifySuccess(this.#translateService.instant(reverse ? 'SUPPLIER_INVOICE_LINE' : 'DO_NOT_INVOICE_SUCCESS'));
      },
      error: (err: HttpErrorResponse) => this.notifyError(err),
    });
  }
  //</editor-fold>

  //<editor-fold desc="Draft creation helpers">
  createDraft(supplierInvoiceItem: SupplierInvoiceItem, section: SupplierInvoiceSection): void {
    if (!section) return;
    const supplierInvoiceSectionId = section['@id'];
    const itemId = supplierInvoiceItem?.['@id'];
    if (!supplierInvoiceSectionId || !itemId) {
      this.notifyError('Missing identifiers.');
      return;
    }

    if (!section.clientOrder) {
      if (section.clientInvoiceDraft) {
        // Draft already exists — add the item directly (draft data is in this.clientInvoiceDraft())
        this.#clientInvoiceDraftItemService
          .createDraftFromSupplierInvoiceSection(supplierInvoiceSectionId, itemId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.notifySuccess(this.#translateService.instant('CLIENT_INVOICE_DRAFT_ITEM'));
              this.updateDraftItems(section);
            },
            error: (err) => this.notifyError(err),
          });
        return;
      }

      this.#dialog = this.#dialogService.open({
        content: ClientInvoiceDraftCreateModalComponent,
        title: this.upper('CONFIRM_DIALOG_TITLE'),
      });
      this.#dialog.content.instance.supplierInvoiceSection.set(section);
      this.#dialog.content.instance.supplierInvoiceItem.set(supplierInvoiceItem);
      this.#dialog.content.instance.createEmptyDraft.set(true);
      this.#dialog.result.pipe(take(1)).subscribe((v: DialogResult) => {
        if (!v.isSuccessFull) return;
        this.updateDraftItems(section);
        this.#coordinator.refreshSectionsList();
      });
      return;
    }

    const draftPayload = {
      client: section.clientOrderClient,
      clientDeliveryAddress: section.clientOrderDeliveryAddress,
      supplierInvoiceSection: supplierInvoiceSectionId,
    };

    this.#clientInvoiceDraftService
      .getForSupplierInvoiceSection(supplierInvoiceSectionId)
      .pipe(
        catchError((err) => (err?.status === 404 ? of(null) : throwError(() => err))),
        switchMap((existing) =>
          existing
            ? of(existing)
            : this.#clientInvoiceDraftService.create(draftPayload as unknown as ClientInvoiceDraft),
        ),
        take(1),
        switchMap(() =>
          this.#clientInvoiceDraftItemService.createDraftFromSupplierInvoiceSection(supplierInvoiceSectionId, itemId),
        ),
        take(1),
      )
      .subscribe({
        next: () => {
          this.notifySuccess(this.#translateService.instant('CLIENT_INVOICE_DRAFT_ITEM'));
          this.updateDraftItems(section);
          // 2) refresh section (includes items) in THIS tab
          this.#coordinator.refreshSection(section['@id']!);

          // 3) tell OTHER tabs that this section changed
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', section['@id']!);
        },
        error: (err) => this.notifyError(err),
      });
  }

  createDrafts(section: SupplierInvoiceSection): void {
    if (!section) return;
    const supplierInvoiceSectionId = section['@id'] as string | undefined;
    if (!supplierInvoiceSectionId) {
      this.notifyError('Missing supplier invoice identifier.');
      return;
    }

    if (!section.clientOrder) {
      this.#dialog = this.#dialogService.open({
        content: ClientInvoiceDraftCreateModalComponent,
        title: this.upper('CREATE_DRAFT_ITEMS'),
      });
      this.#dialog.content.instance.supplierInvoiceSection.set(section);
      this.#dialog.content.instance.supplierInvoice.set(this.supplierInvoice());
      // this.#dialog.content.instance.createEmptyDraft.set(true);
      this.#dialog.result.pipe(take(1)).subscribe((v: DialogResult) => {
        if (!v.isSuccessFull) return;
        this.updateDraftItems(section);
      });
      return;
    }

    const draftPayload = {
      client: section.clientOrderClient,
      clientDeliveryAddress: section.clientOrderDeliveryAddress,
      supplierInvoiceSection: supplierInvoiceSectionId,
    };

    this.#clientInvoiceDraftItemService
      .createFromSupplierInvoiceSection(supplierInvoiceSectionId, draftPayload as unknown as ClientInvoiceDraftItem)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notifySuccess(this.#translateService.instant('CLIENT_INVOICE_DRAFT_ITEMS'), true);
          this.updateDraftItems(section);
        },
        error: (err) => this.notifyError(err),
      });
  }

  deleteDrafts() {
    const sections = this.sections();
    if (!sections) return;
    const sectionIri = sections[0]['@id']!;

    this.#clientInvoiceDraftService
      .getForSupplierInvoiceSection(sectionIri)
      .pipe(
        take(1),
        switchMap((draft: any) => {
          if (!draft) {
            this.notifyError('Missing draft.');
            return throwError(() => new Error('No draft found for this supplier invoice.'));
          }
          const draftIri = `/client_invoice_drafts/${draft.id}`;
          return this.#clientInvoiceDraftService.delete(draftIri);
        }),
      )
      .subscribe({
        next: () => {
          this.notifyDelete(this.#translateService.instant('CLIENT_INVOICE_DRAFT_ITEMS'));
          this.#getFromBackEnd(true);

          sections.forEach((sec) => this.#tracker.broadcastItemTopicChanged('supplierInvoice', sec['@id']!));
        },
        error: (err) => this.notifyError(err),
      });
  }

  updateSupplierInvoice(field: 'internalNote') {
    this.#updateInvoice$.next(field);
  }

  updateDraftItems(section: SupplierInvoiceSection): void {
    const sectionIri = section['@id'] as string;
    if (!sectionIri) {
      this.notifyError('Missing supplier invoice section identifier.');
      return;
    }

    this.#clientInvoiceDraftItemService
      .getForSupplierInvoiceSection([sectionIri])
      .pipe(take(1))
      .subscribe({
        next: (bySection) => {
          const draftsForSection = bySection[sectionIri] ?? [];
          this.setDraftItemsForSection(sectionIri, draftsForSection, true);
          this.#coordinator.refreshSection(sectionIri);
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', sectionIri);
        },
        error: (err) => this.notifyError(err),
      });
  }
  //</editor-fold>

  //<editor-fold desc="Mapping forms (reworked to use MappingFormsManager)">
  private syncMappingFormsFromSections() {
    this.mappingFormsManager.syncFromSections(
      this.supplierInvoiceItemMappingsSections(),
      (m) => this.getMaxAllowedAmount(m),
      this.supplierInvoiceIsLocked(),
    );
  }

  onMappingAmountBlur(mapping: SupplierInvoiceItemMapping): void {
    const form = this.mappingFormsManager.get(mapping['@id']!);
    if (!form || form.invalid) return;

    const nextAmt = form.value.amount ?? 0;
    if (nextAmt === mapping.amount) return;

    this.#supplierInvoiceItemMappingService
      .update(mapping['@id']!, { ...mapping, amount: nextAmt })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notifySuccess(this.translationService.instant('SUPPLIER_INVOICE_ITEM_MAPPING'));
          // Mercure broadcasts mapping + draft-item updates automatically after a PATCH.
          // Explicit refreshes here are redundant and cause SSE reconnect cascades
          // because each signal update re-triggers allEntitiesToWatch → subscribeToItems.
        },
        error: (err) => this.notifyError(err),
      });
  }

  getMaxForDraftItem(draftItem: ClientInvoiceDraftItem): number {
    const section = this.sections()?.[0];
    if (!section?.clientOrder) return Infinity;
    if (this.draftAmountReadOnlyByIri().get(draftItem['@id']!)) return Infinity;
    // draftItem.supplierInvoiceItem is absent from the API response; find the invoice item
    // via the mapping that references this draft item.
    const mapping = this.allSupplierInvoiceMappings().find((m) => m.clientInvoiceDraftItem === draftItem['@id']!);
    if (!mapping) return Infinity;
    const invoiceItem = this.allSupplierInvoiceItems().find((i) => i['@id'] === mapping.supplierInvoiceItem);
    return invoiceItem?.amount ?? Infinity;
  }
  //</editor-fold>

  //<editor-fold desc="Linked / mapping state helpers">
  // Still inside SupplierInvoiceDetailComponent

  isSectionDataLoading(section: SupplierInvoiceSection): boolean {
    const iri = section['@id']!;
    const items = this.supplierInvoiceItemsSections();
    const mappings = this.supplierInvoiceItemMappingsSections();
    const client = this.clientOrderItemsSections();
    const drafts = this.clientInvoiceDraftItemsSections();

    // If any of the maps is still null/undefined -> loading
    if (!items || !mappings || !client || !drafts) return true;

    const hasItems = Array.isArray(items[iri]);
    const hasMappings = Array.isArray(mappings[iri]);
    const hasClient = Array.isArray(client[iri]);
    const hasDrafts = Array.isArray(drafts[iri]);

    // consider "loaded but empty" as not loading -> they will see the "no items" UI
    return !(hasItems && hasMappings && hasClient && hasDrafts);
  }

  isClientOrderListLoading(): boolean {
    const sections = this.sections();
    if (!sections || sections.length === 0) return true;
    const firstIri = sections[0]['@id']!;

    const client = this.clientOrderItemsSections();
    if (!client) return true;

    // again: undefined → not loaded; [] → loaded but empty
    return !Array.isArray(client[firstIri]);
  }

  isSectionLinked(section: SupplierInvoiceSection): boolean {
    const iri = section['@id']!;
    const override = this.linkedSectionOverrides().get(iri);
    if (override !== undefined) return override;
    return this.sectionHasMappings().get(iri) ?? false;
  }

  toggleSectionChecked(sectionIri: string): void {
    this.checkedSectionIris.update((prev) => {
      const next = new Set(prev);
      if (next.has(sectionIri)) next.delete(sectionIri);
      else next.add(sectionIri);
      return next;
    });
  }

  private setDraftItemsForSection(sectionIri: string, drafts: ClientInvoiceDraftItem[], broadcast = false): void {
    this.clientInvoiceDraftItemsSections.update((sections) => setSectionValues(sections, sectionIri, drafts));

    if (broadcast) {
      this.#tracker.broadcastItemTopicChanged('supplierInvoice', `${sectionIri}/draft_items`);
    }
  }

  isSectionAllDoNotInvoice(section: SupplierInvoiceSection): boolean {
    return this.allDoNotInvoiceBySection().get(section['@id']!) ?? false;
  }

  isSectionFullyMapped(section: SupplierInvoiceSection): boolean {
    return this.fullyMappedBySection().get(section['@id']!) ?? false;
  }

  getSupplierInvoiceItemMappingState(item: SupplierInvoiceItem, section?: SupplierInvoiceSection): SectionItemStatus {
    if (item?.doNotInvoice) return SectionItemStatus.COMPLETE;

    const sectionIri = section?.['@id'] ?? this.itemSectionByIri().get(item['@id']!) ?? null;
    if (!sectionIri) return SectionItemStatus.UNMAPPED;

    const arr = this.mappingsBySectionByItem().get(sectionIri)?.get(item['@id']!) ?? [];

    const mappedTotal = roundAmount(
      arr.reduce((sum, row: any) => {
        const m = row.mapping ?? row;
        return sum + (Number(m?.amount) || 0);
      }, 0),
    );

    const required = roundAmount(Number(item.amount) || 0);

    if (mappedTotal === required) return SectionItemStatus.COMPLETE;
    if (mappedTotal >= 1) return SectionItemStatus.PARTIAL;
    return SectionItemStatus.UNMAPPED;
  }

  isItemFullyMapped(item: SupplierInvoiceItem, section?: SupplierInvoiceSection): boolean {
    return this.getSupplierInvoiceItemMappingState(item, section) === SectionItemStatus.COMPLETE;
  }

  isItemPartiallyMapped(item: SupplierInvoiceItem, section?: SupplierInvoiceSection): boolean {
    return this.getSupplierInvoiceItemMappingState(item, section) === SectionItemStatus.PARTIAL;
  }

  hasSectionAnyMappingOrDoNotInvoice(section: SupplierInvoiceSection): boolean {
    return this.hasActivityBySection().get(section['@id']!) ?? false;
  }

  openAllSections() {
    this.sectionOpenStatus.update((m) => {
      const next = new Map(m);
      for (const key of next.keys()) next.set(key, true);
      return next;
    });
  }

  closeAllSections() {
    this.sectionOpenStatus.update((m) => {
      const next = new Map(m);
      for (const key of next.keys()) next.set(key, false);
      return next;
    });
  }

  onAllToggleClick() {
    if (this.allSectionsClosed()) return this.openAllSections();
    if (this.allSectionsOpen()) return this.closeAllSections();
    return this.currentToggleState().action();
  }

  toggleSection(id: string) {
    const current = new Map(this.sectionOpenStatus());
    current.set(id, !current.get(id));
    this.sectionOpenStatus.set(current);
  }
  //</editor-fold>

  //<editor-fold desc="Tabs">
  onTabSelect(e: SelectEvent) {
    this.selectedTab.set(e.index);
  }

  getColorClass(item: ClientOrderItem): string {
    return this.isClientItemFullyMapped(item) ? 'status-item-processed' : 'status-item-open';
  }

  isClientItemFullyMapped(item: ClientOrderItem): boolean {
    return Number(item.mappedAmount ?? 0) >= Number(item.amount ?? 0);
  }
  //</editor-fold>

  //<editor-fold desc="Actions: menus">
  onActionMenuItemClick(
    actionMenuItem: ActionListItemModel<ActionMenuItemAction | string>,
    item: SupplierInvoiceItem,
    supplierInvoiceSection: SupplierInvoiceSection,
  ): void {
    switch (actionMenuItem.action) {
      case ActionMenuItemAction.DUPLICATE:
        this.createDraft(item, supplierInvoiceSection);
        break;
      case ActionMenuItemAction.DO_NOT_INVOICE:
        this.doNotInvoice(item);
        break;
      case ActionMenuItemAction.DO_INVOICE:
        this.doNotInvoice(item, true);
        break;
      case ActionMenuItemAction.ASSOCIATE:
        this.linkOrderItem(item, supplierInvoiceSection);
        break;
      case ActionMenuItemAction.COPY:
        this.#copySupplierInvoiceOrItem(item);
        break;
    }
  }

  onActionMappedMenuItemClick(
    actionMenuItem: ActionListItemModel<ActionMenuItemAction | string>,
    item: SupplierInvoiceItemMapping,
    supplierInvoiceSection: SupplierInvoiceSection,
  ): void {
    if (actionMenuItem.action === ActionMenuItemAction.DELETE) {
      this.deleteLinkedItem(item, supplierInvoiceSection);
    }
  }

  #copySupplierInvoiceOrItem(item?: SupplierInvoiceItem) {
    const copy: StoredSupplierCreditNoteItemLink = {
      documentType: SupplierCreditNoteMappingType.SUPPLIER_INVOICE,
      documentIri: this.supplierInvoice()['@id']!,
    };

    if (item) {
      copy.itemType = SupplierCreditNoteMappingType.SUPPLIER_INVOICE_ITEM;
      copy.itemIri = item['@id']!;
    }

    localStorage.removeItem('supplierCreditNoteLink');
    localStorage.setItem('supplierCreditNoteLink', JSON.stringify(copy));
  }

  #openCreateSupplierComplaintDialog() {
    this.dialog = this.dialogService.open({
      content: CreateSupplierComplaintModalComponent,
      title: this.capitalizePipe.transform(
        this.translationService.instant('GENERATE_ENTITY', {
          entity: this.translationService.instant('SUPPLIER_COMPLAINT'),
        }),
      ),
      width: 1200
    });

    this.dialog.content.instance.supplierInvoice.set(this.supplierInvoice());
  }

  getSupplierInvoiceItemMappingsForItem(
    item: SupplierInvoiceItem,
    section: SupplierInvoiceSection,
  ): SupplierInvoiceItemMapping[] {
    const sectionIri = section['@id']!;
    const all = this.supplierInvoiceItemMappingsSections()?.[sectionIri] ?? [];

    const byItem = all.filter((m) => m.supplierInvoiceItem === item['@id']);
    if (byItem.length) return byItem;

    const ids = item.supplierInvoiceItemMappings ?? [];
    if (!Array.isArray(ids) || ids.length === 0) return [];
    return all.filter((m) => ids.includes(m['@id']!));
  }

  enterPredicate: (drag: any, drop: CdkDropList) => boolean = (_drag, drop) => {
    const numericId = drop.id.replace('invoiceLine-', '');
    const target = (this.allSupplierInvoiceItems() ?? []).find((i) => String(i.id) === numericId);
    if (!target) return false;
    return !target.doNotInvoice && this.droppableItemIris().has(target['@id']!);
  };

  getMaxAllowedAmount(mapping: SupplierInvoiceItemMapping): number {
    const sectionIri = this.itemSectionByIri().get(mapping.supplierInvoiceItem);
    if (!sectionIri) return Number(mapping.amount) || 1;
    const sectionItems = this.supplierInvoiceItemsSections()?.[sectionIri] ?? [];
    const invoiceItem = sectionItems.find((i) => i['@id'] === mapping.supplierInvoiceItem);
    return invoiceItem ? invoiceItem.amount : Number(mapping.amount) || 1;
  }

  hasMappingAmountOverflow(mapping: SupplierInvoiceItemMapping): { show: boolean; ordered: number; mapped: number } {
    const empty = { show: false, ordered: 0, mapped: 0 };
    const sectionIri = this.itemSectionByIri().get(mapping.supplierInvoiceItem);
    if (!sectionIri) return empty;
    const sectionMappings = this.supplierInvoiceItemMappingsSections()?.[sectionIri] ?? [];
    const clientItem = this.clientOrderItemsBySectionByIri()
      .get(sectionIri)
      ?.get(mapping.clientOrderItem ?? '');
    if (!clientItem) return empty;
    const totalMapped = roundAmount(
      (sectionMappings as SupplierInvoiceItemMapping[])
        .filter((m) => m.clientOrderItem === clientItem['@id'])
        .reduce((sum, m) => sum + (Number(m.amount) || 0), 0),
    );
    const ordered = Number(clientItem.amount);
    return { show: totalMapped > ordered, ordered, mapped: totalMapped };
  }
  //</editor-fold>

  //<editor-fold desc="Price mismatch">
  hasPriceMismatch(item: SupplierInvoiceItem, section: SupplierInvoiceSection) {
    const sectionIri = section['@id']!;
    const byItem = this.mappingsBySectionByItem().get(sectionIri)?.get(item['@id']!) ?? [];
    if (byItem.length === 0) return { show: false, ordered: 0, invoiced: 0, diff: 0 };

    // the ordered unit price: the purchase price of the linked client order lines, weighted by the linked amounts
    // (a line split over several client order lines must not add up their unit prices)
    let orderedTotal = 0;
    let linkedAmount = 0;
    for (const row of byItem) {
      const coi = row.clientOrderItem;
      const amount = Number(row.mapping.amount) || 0;
      orderedTotal += Number(coi?.purchasePrice ?? 0) * amount;
      linkedAmount += amount;
    }
    const ordered = linkedAmount
      ? orderedTotal / linkedAmount
      : byItem.reduce((sum, row) => sum + Number(row.clientOrderItem?.purchasePrice ?? 0), 0) / byItem.length;

    const invoiced = Number(item.purchasePrice ?? 0);
    const diff = ordered - invoiced;
    const show = diff >= 0.01 && item.supplierInvoiceItemMappings.length > 0;
    return { show, ordered, invoiced, diff };
  }

  showPopup(el: PopoverAnchorDirective) {
    showPopupTimout(el, 50);
  }

  hidePopup(el: PopoverAnchorDirective) {
    stopOrRemovePopup(el, 50);
  }
  //</editor-fold>

  //<editor-fold desc="Nav">
  previous() {
    const st = this.state();
    if (!st || st.index <= 0) return;
    const listContext = { ...st, index: st.index - 1 };
    this.#navigationService.setListContext(listContext);
    this.router.navigate([`/purchase${listContext.idsOnPage[listContext.index]}`], { state: { listContext } });
  }

  next() {
    const st = this.state();
    if (!st || st.index >= st.idsOnPage.length - 1) return;
    const listContext = { ...st, index: st.index + 1 };
    this.#navigationService.setListContext(listContext);
    this.router.navigate([`/purchase${listContext.idsOnPage[listContext.index]}`], { state: { listContext } });
  }

  cancelButton() {
    this.router.navigate(['/purchase/supplier_invoices'], {
      queryParams: this.returnParams,
      state: { isRedirect: true },
    });
  }

  onOverviewContextMenu(event: MouseEvent): void {
    event.preventDefault();
    this.#menu.openMenu(['purchase/supplier_invoices'], event.clientX, event.clientY, {
      queryParams: this.returnParams,
    });
  }

  onAuxClick(event: PointerEvent): void {
    event.preventDefault();
    this.#menu.openInNewTab(['purchase/supplier_invoices'], { queryParams: this.returnParams });
    return;
  }
  //</editor-fold>

  //<editor-fold desc="Totals / refresh">
  updateTotals() {
    this.#supplierInvoiceService.get(this.supplierInvoice()!['@id']!).subscribe({
      next: (invoice) => this.supplierInvoice.set(invoice),
    });
  }

  getInvoice(value: boolean, result: SupplierInvoiceSection) {
    if (!value) return;

    this.fetchForSection(result)
      .pipe(take(1))
      .subscribe({
        next: (payload) => {
          this.replaceSectionData(result, {
            items: payload.items,
            mappings: payload.mappings,
            clientOrderItems: payload.clientOrderItems,
            draftItems: payload.draftItems,
          });

          this.#tracker.broadcastItemTopicChanged('supplierInvoice', '/supplier_invoice_sections');
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', result['@id']!);
          this.#coordinator.refreshSection(result['@id']!);
          this.updateTotals();
        },
        error: (err) => this.notifyError(err),
      });
  }

  getInvoiceItemMapping(_value: boolean, supplierInvoiceItemIri: string, section: SupplierInvoiceSection) {
    this.fetchForSupplierInvoiceItem(supplierInvoiceItemIri)
      .pipe(take(1))
      .subscribe({
        next: (payload) => {
          this.replaceSectionItemData(section, supplierInvoiceItemIri, payload);
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', section['@id']!);
        },
        error: (err) => this.notifyError(err),
      });
  }

  deleteInvoiceItemMapping(supplierInvoiceItemMapping: SupplierInvoiceItemMapping, section: SupplierInvoiceSection) {
    this.#tracker.requestUnsubscribeItemAcrossTabs('supplierInvoice', supplierInvoiceItemMapping['@id']!);

    const lineIri = supplierInvoiceItemMapping.supplierInvoiceItem;

    this.#supplierInvoiceItemMappingService
      .delete(supplierInvoiceItemMapping['@id']!)
      .pipe(
        switchMap(() =>
          forkJoin({
            mappings: this.#supplierInvoiceItemMappingService.getForSupplierInvoiceItem(lineIri).pipe(take(1)),
            item: this.#supplierInvoiceItemService.get(lineIri).pipe(take(1)),
          }),
        ),
      )
      .subscribe({
        next: ({ mappings, item }) => {
          this.replaceSectionItemData(section, lineIri, { mappings });
          this.replaceSectionLine(section, item);
          this.#coordinator.refreshSectionDraftItems(section['@id']!);
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', lineIri);
          this.#tracker.broadcastItemTopicChanged('supplierInvoice', section['@id']!);

          this.notifyDelete(this.translationService.instant('SUPPLIER_INVOICE_ITEM_MAPPING'));
        },
        error: (err) => {
          // eslint-disable-next-line no-debugger
          this.notifyError(err);
        },
      });
  }
  //</editor-fold>

  //<editor-fold desc="Mapping ui helpers">
  isMapping(data: any): data is SupplierInvoiceItemMapping {
    return data && typeof data === 'object' && 'supplierInvoiceItem' in data;
  }
  //</editor-fold>

  //<editor-fold desc="State update helpers">
  private endDrag() {
    requestAnimationFrame(() => this.isDraggingMappedClientItem.set(false));
  }

  private upper(key: string) {
    return this.#translateService.instant(key, { entity: '' }).toUpperCase();
  }

  private setTab(i: number) {
    if (this.selectedTab() !== i) this.selectedTab.set(i);
  }

  private notifySuccess(value: string, plural = false) {
    const n = this.#notificationService.show(successSaveToast);
    if (n.content) {
      n.content.instance.value = value;
      (n.content.instance as any).plural = plural;
    }
  }

  private notifyDelete(value: string) {
    const n = this.#notificationService.show(successDeleteToast);
    if (n.content) n.content.instance.value = value;
  }

  private notifyError(err: any) {
    const n = this.#notificationService.show(errorToast);
    if (n.content) n.content.instance.setCurrentValue(err);
  }

  private confirm(titleKey: string, message?: string): Observable<boolean> {
    const title = this.upper(titleKey);
    this.#dialog = this.#dialogService.open({ content: ConfirmDialogComponent, title });
    if (message) this.#dialog.content.instance.message = message;
    return this.#dialog.result.pipe(
      take(1),
      map((r: DialogResult) => !!r?.isSuccessFull),
    );
  }
  //</editor-fold>

  //<editor-fold desc="Draft / invoice creation flow">
  #createClientInvoice(): void {
    let create: (iri?: string) => void;
    const clientInvoiceDraft = this.allClientInvoiceDraftItems()?.[0]?.clientInvoiceDraft;

    // Single section → always create invoice from the draft items
    const createFromDraft = !this.isMulti() && !!clientInvoiceDraft;
    if (createFromDraft) {
      create = () => {
        this.#clientInvoiceDraftService.createInvoice(clientInvoiceDraft!).subscribe({
          next: (result) => {
            this.notifySuccess(this.#translateService.instant('CLIENT_INVOICE'));
            this.supplierInvoice.update((inv) => ({
              ...inv,
              clientInvoices: [...(inv.clientInvoices ?? []), result['@id']!],
            }));
            this.#coordinator.refreshInvoiceEntity();
            const tree = this.router.createUrlTree([`sales/client_invoices`]);
            const url = `${window.location.origin}${this.router.serializeUrl(tree)}?supplierInvoice=${this.supplierInvoice()!['@id']}`;
            window.open(url, '_blank', 'noopener');
          },
          error: (err) => this.notifyError(err),
        });
      };
    } else {
      const inv: ClientInvoice = {
        supplierInvoiceItemMappings: this.checkedSectionMappings().map((x) => x['@id']),
        supplierInvoice: this.supplierInvoice()['@id'],
      } as unknown as ClientInvoice;

      create = () =>
        this.#clientInvoiceService.createFromMappings(inv).subscribe({
          next: (result) => {
            this.notifySuccess(this.#translateService.instant('CLIENT_INVOICE'));
            this.supplierInvoice.update((inv) => ({
              ...inv,
              clientInvoices: [...(inv.clientInvoices ?? []), result['@id']!],
            }));
            const processedSectionIris = [...this.checkedSectionIris()];
            this.#coordinator.refreshSectionsList();
            processedSectionIris.forEach((iri) => this.#coordinator.refreshSection(iri));
            this.#coordinator.refreshInvoiceEntity();
            const tree = this.router.createUrlTree([`sales/client_invoices`]);
            const url = `${window.location.origin}${this.router.serializeUrl(tree)}?supplierInvoice=${this.supplierInvoice()!['@id']}`;
            window.open(url, '_blank', 'noopener');
          },
          error: (err) => this.notifyError(err),
        });
    }
    create();
  }

  //</editor-fold>

  //<editor-fold desc="Server refresh">
  #getFromBackEnd(withLoader = false): void {
    this.#getFromBackEndSub?.unsubscribe();
    this.isLoading.set(withLoader);

    this.#getFromBackEndSub = forkJoin({
      invoiceItems: this.#supplierInvoiceItemService
        .getForSupplierInvoiceSection(this.supplierInvoice().supplierInvoiceSections)
        .pipe(take(1)),
      mappings: this.#supplierInvoiceItemMappingService
        .getForSupplierInvoiceSection(this.supplierInvoice().supplierInvoiceSections)
        .pipe(take(1)),
      clientItems: this.#clientOrderItemService.getForSupplierInvoiceSection(
        this.supplierInvoice().supplierInvoiceSections,
      ),
      clientInvoiceDraftItems: this.#clientInvoiceDraftItemService.getForSupplierInvoiceSection(
        this.supplierInvoice().supplierInvoiceSections,
      ),
      sections: this.#supplierInvoiceSectionsService.getForSupplierInvoice(this.supplierInvoice()['@id']!),
    }).subscribe({
      next: ({ invoiceItems, mappings, clientItems, clientInvoiceDraftItems, sections }) => {
        this.supplierInvoiceItemsSections.set(invoiceItems);
        this.supplierInvoiceItemMappingsSections.set(mappings);
        this.clientOrderItemsSections.set(clientItems);
        this.clientInvoiceDraftItemsSections.set(clientInvoiceDraftItems);
        this.sections.set(sections);
        this.syncMappingFormsFromSections();

        this.isLoading.set(false);
      },
      error: (err) => {
        this.notifyError(err);
        this.isLoading.set(false);
      },
    });
  }

  #loadClientInvoiceItems(): void {
    const invoice = this.supplierInvoice();
    if (!invoice) return;

    const iris = invoice.clientInvoices ?? [];
    if (iris.length === 0) return;

    forkJoin({
      items: forkJoin(iris.map((iri) => this.#clientInvoiceItemService.getForClientInvoice(iri).pipe(take(1)))),
      invoices: this.#clientInvoiceService.getForIris(iris),
    }).subscribe({
      next: ({ items, invoices }) => {
        this.clientInvoiceItems.set(items.flat());
        this.clientInvoices.set(invoices);
      },
      error: () => {
        this.clientInvoiceItems.set([]);
        this.clientInvoices.set([]);
      },
    });
  }
  //</editor-fold>

  //<editor-fold desc="Mapping core (delegates to SupplierInvoiceMappingOps)">
  #performDrop(
    targetLine: SupplierInvoiceItem,
    section: SupplierInvoiceSection,
    draggedIsMapping: boolean,
    mappingId: string | undefined,
    clientOrderItemId: string,
    clientItem: ClientOrderItem,
    previousSupplierInvoiceItemMappingAmount?: number,
  ): void {
    this.#mappingOps.dropOnInvoiceLine({
      targetLine,
      sectionIri: section['@id']!,
      draggedIsMapping,
      mappingId,
      clientOrderItemId,
      clientItem,
      previousMappingAmount: previousSupplierInvoiceItemMappingAmount,
      getItemMappingsForTarget: () => this.getSupplierInvoiceItemMappingsForItem(targetLine, section),
      adjustClientItemMappedAmount: (iri: string, delta: number) => this.adjustClientItemMappedAmount(iri, delta),
      syncMappingFormsFromSections: () => this.syncMappingFormsFromSections(),
    });

    if (this.allClientItemsMapped() && this.hasDraftOrInvoiceItems()) {
      this.setTab(1);
    }
  }

  // #hasDraftForSupplierItem(supplierInvoiceItem: SupplierInvoiceItem): boolean {
  //   const typeNumber = supplierInvoiceItem?.typeNumber;
  //   if (!typeNumber) return false;
  //   const draftItems = this.allClientInvoiceDraftItems?.();
  //   if (!draftItems) return false;
  //   return draftItems.some((draft) => draft.typeNumber === typeNumber);
  // }
  #hasDraftForSupplierItem(supplierInvoiceItem: SupplierInvoiceItem): boolean {
    const itemIri = supplierInvoiceItem?.['@id'];
    if (!itemIri) return false;

    const bySection = this.mappingsBySectionByItem(); // Map<sectionIri, Map<itemIri, JoinedMapping[]>>

    for (const [, byItem] of bySection) {
      const list = byItem.get(itemIri);
      if (!list) continue;

      if (
        list.some((row) => {
          const m = row.mapping ?? row;
          return !!m.clientInvoiceDraftItem;
        })
      ) {
        return true;
      }
    }

    return false;
  }

  //</editor-fold>

  //<editor-fold desc="Forms and helpers">
  /**
   * Shows the note of the invoice. For the same invoice, a note the user has edited but not saved yet (a dirty
   * control) is kept; another invoice always replaces it.
   */
  #patchForm(supplierInvoice: SupplierInvoice) {
    const otherInvoice = supplierInvoice['@id'] !== this.#notesInvoiceIri;
    this.#notesInvoiceIri = supplierInvoice['@id'];
    const { internalNote } = this.supplierInvoiceForm.controls;
    if (otherInvoice || !internalNote.dirty) internalNote.setValue(supplierInvoice.internalNote ?? '');
    if (otherInvoice) this.supplierInvoiceForm.markAsPristine();
  }

  private sum(nums: number[]) {
    return nums.reduce((s, n) => s + (Number(n) || 0), 0);
  }
  //</editor-fold>

  //<editor-fold desc="Breadcrumbs">
  #setBreadCrumbs() {
    this.breadcrumbService.loading = true;
    const params = { ...this.returnParams, state: { isRedirect: true } };
    this.breadcrumbService.breadcrumbsSubject.next([
      {
        label: 'SUPPLIER_INVOICES',
        path: '/purchase/supplier_invoices',
        params,
      },
    ]);
    this.breadcrumbService.loading = false;
  }
  //</editor-fold>

  //<editor-fold desc="Layout measurement">
  private setupTabMeasurement(el: HTMLElement, doc: HTMLElement) {
    this.teardownTabMeasurement();
    queueMicrotask(() => this.tabHeight.set(Math.round(el.getBoundingClientRect().height)));
    queueMicrotask(() => this.docContentHeight.set(Math.round(doc.getBoundingClientRect().height)));
    this.#dCo = new ResizeObserver((entries) => {
      const dh = Math.round(entries[0].contentRect.height);
      this.docContentHeight.set(dh);
    });
    this.#dCo.observe(doc);
    this.#to = new ResizeObserver((entries) => {
      const th = Math.round(entries[0].contentRect.height);
      this.tabHeight.set(th);
    });
    this.#to.observe(el);
  }

  private teardownTabMeasurement() {
    this.#to?.disconnect();
    this.#to = undefined;
    this.#dCo?.disconnect();
    this.#dCo = undefined;
  }
  //</editor-fold>

  //<editor-fold desc="Fetch helpers and replacement functions">
  private fetchForSection(section: SupplierInvoiceSection) {
    const one = [section['@id']!];
    return forkJoin({
      items: this.#supplierInvoiceItemService.getForSupplierInvoiceSection(one).pipe(take(1)),
      mappings: this.#supplierInvoiceItemMappingService.getForSupplierInvoiceSection(one).pipe(take(1)),
      clientOrderItems: this.#clientOrderItemService.getForSupplierInvoiceSection(one).pipe(take(1)),
      draftItems: this.#clientInvoiceDraftItemService.getForSupplierInvoiceSection(one).pipe(take(1)),
    });
  }

  private fetchForSupplierInvoiceItem(supplierInvoiceItemIri: string) {
    return forkJoin({
      mappings: this.#supplierInvoiceItemMappingService.getForSupplierInvoiceItem(supplierInvoiceItemIri).pipe(take(1)),
    });
  }

  private replaceSectionData(
    section: SupplierInvoiceSection,
    data: {
      items: DocumentSections<SupplierInvoiceItem>;
      mappings: DocumentSections<SupplierInvoiceItemMapping>;
      clientOrderItems: DocumentSections<ClientOrderItem>;
      draftItems: DocumentSections<ClientInvoiceDraftItem>;
    },
  ) {
    const iri = section['@id'] as string;
    this.supplierInvoiceItemsSections.update((sections) => setSectionValues(sections, iri, data.items[iri] ?? []));
    this.supplierInvoiceItemMappingsSections.update((sections) =>
      setSectionValues(sections, iri, data.mappings[iri] ?? []),
    );
    this.clientOrderItemsSections.update((sections) =>
      setSectionValues(sections, iri, data.clientOrderItems[iri] ?? []),
    );
    this.clientInvoiceDraftItemsSections.update((sections) =>
      setSectionValues(sections, iri, data.draftItems[iri] ?? []),
    );

    this.sections.update((list) => replaceByIri(list, section));
  }

  private replaceSectionLine(section: SupplierInvoiceSection, updatedItem: SupplierInvoiceItem) {
    const iri = section['@id'] as string;

    this.supplierInvoiceItemsSections.update((sections) => {
      const current = (sections?.[iri] ?? []) as SupplierInvoiceItem[];
      const next = current.map((it) => (it['@id'] === updatedItem['@id'] ? updatedItem : it));
      return setSectionValues(sections, iri, next);
    });
  }

  private replaceSectionItemData(
    section: SupplierInvoiceSection,
    supplierInvoiceItemIri: string,
    data: { mappings: SupplierInvoiceItemMapping[] },
  ) {
    const iri = section['@id'] as string;

    this.supplierInvoiceItemMappingsSections.update((sections) => {
      const current = (sections?.[iri] ?? []) as SupplierInvoiceItemMapping[];

      const kept = current.filter((m) => m.supplierInvoiceItem !== supplierInvoiceItemIri);
      const next = [...kept, ...(data.mappings ?? [])];

      return setSectionValues(sections, iri, next);
    });

    this.syncMappingFormsFromSections();
  }

  //</editor-fold>

  //<editor-fold desc="Mercure integration">
  private allEntitiesToWatch(): Entity[] {
    const out: Entity[] = [];

    const inv = this.supplierInvoice();
    if (inv?.['@id']) out.push(inv as unknown as Entity);

    const sections = this.sections() ?? [];
    sections.forEach((s) => s?.['@id'] && out.push(s as unknown as Entity));

    (this.allSupplierInvoiceItems() ?? []).forEach((it) => it?.['@id'] && out.push(it as unknown as Entity));
    (this.allSupplierInvoiceMappings() ?? []).forEach((m) => m?.['@id'] && out.push(m as unknown as Entity));

    const coiBySection = this.clientOrderItemsSections() ?? {};
    Object.values(coiBySection).forEach((arr) =>
      (arr ?? []).forEach((coi) => coi?.['@id'] && out.push(coi as unknown as Entity)),
    );

    (this.allClientInvoiceDraftItems() ?? []).forEach((d) => d?.['@id'] && out.push(d as unknown as Entity));

    return out;
  }
  //</editor-fold>
  protected readonly BadgeColor = BadgeColor;

  protected togglePanel(event: any, panel: ExpansionPanelComponent) {
    event.preventDefault();
    if (this.isMulti()) {
      panel.expanded = panel.expanded;
    } else {
      panel.expanded = !panel.expanded;
    }
  }
}

interface SupplierInvoiceForm {
  internalNote: FormControl<string | null>;
}
